import React, { useState, useCallback, useRef, useEffect } from 'react'
import classNames from 'classnames'
import { updateEventStatus } from '../../../api/events'
import { useTranslation } from 'react-i18next'
import { useOutsideCheckClick } from '../../../hooks/UseOnClickOutside'
import { useSelector } from 'react-redux'
import { RemoveIcon } from 'images'

// icons
// import flag from '../../images/icons/flag.svg'
// import cancelled from '../../images/icons/cancelled.svg'
// import hidden from '../../images/icons/hidden.svg'

// loading spinner
import LoadingSpinner, { SpinnerSize } from '../../Shared/LoadingSpinner'

import { hasRestrictedAccessFor } from '../../../utils/helpers'

// const IMG_SRCS = {
//     'FLAGGED': flag,
//     'CANCELLED': cancelled,
//     'HIDDEN': hidden,
//     'DEFAULT': null
// }

const StatusOption = ({ value, currentStatus, handleChange, loading }) => {
    const { t } = useTranslation()
    const user = useSelector((store) => store.user)

    const handleValueChange = useCallback(
        (e) => {
            if (hasRestrictedAccessFor(user.accessLevel, 'actions', 'canChangeEventStatus')) return

            if (loading) return

            handleChange(value, e)
        },
        [value, handleChange, loading, user.accessLevel],
    )

    return (
        <div
            onClick={handleValueChange}
            className={
                classNames(
                    'events-insights-page__status_menu_dropdown-item',
                    value === currentStatus ? 'selected' : '',
                    loading ? 'loading' : '',
                )
            }
        >
            <span className="capitalized">{t(`Dashboard.eventStatusSelect.${value.toLowerCase()}`)}</span>
        </div>
    )
}

const StatusDropdown = ({ handleChange, currentStatus, menuOpen, loading }) => {
    const user = useSelector((store) => store.user)

    if (hasRestrictedAccessFor(user.accessLevel, 'actions', 'canChangeEventStatus')) return null

    return (
        <div className={`events-insights-page__status_menu_dropdown-container ${menuOpen ? 'open' : ''}`}>
            <StatusOption loading={loading} currentStatus={currentStatus} handleChange={handleChange} value="FLAGGED" />
            <StatusOption loading={loading} currentStatus={currentStatus} handleChange={handleChange} value="HIDDEN" />
            <StatusOption
                loading={loading}
                currentStatus={currentStatus}
                handleChange={handleChange}
                value="CANCELLED"
            />
            {loading ? (
                <div
                    style={{ position: 'absolute', margin: 'auto' }}
                    className={'events-insights-page__status_menu_spinner-container'}
                >
                    <LoadingSpinner portalBackground={false} showText={false} size={SpinnerSize.SMALL} />
                </div>
            ) : null}
        </div>
    )
}

const StatusSelectMenu = ({ hideMobile = false, eventId, frontendStatus }) => {
    const { t } = useTranslation()
    const user = useSelector((store) => store.user)

    const [loading, setLoading] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const [currentStatus, setCurrentStatus] = useState(frontendStatus)
    const [error, setError] = useState(null)

    const menuRef = useRef(null)

    useOutsideCheckClick(menuRef, setMenuOpen, menuOpen)

    useEffect(() => {
        setCurrentStatus(frontendStatus)
    }, [frontendStatus])

    const handleMenuOpen = () => {
        if (hasRestrictedAccessFor(user.accessLevel, 'actions', 'canChangeEventStatus')) return
        setMenuOpen((prev) => !prev)
    }

    const handleChange = useCallback(
        async (newStatus, event) => {
            event.preventDefault()

            if (hasRestrictedAccessFor(user.accessLevel, 'actions', 'canChangeEventStatus')) return

            if (loading) return

            if (newStatus === currentStatus) {
                setMenuOpen(false)
                return
            }

            setLoading(true)

            try {
                const updatedStatus = await updateEventStatus(eventId, newStatus)
                if (updatedStatus.error) {
                    setError(`Error: ${updatedStatus.error}`)
                } else if (updatedStatus.success) {
                    setError(null)
                    setCurrentStatus(newStatus)
                    setMenuOpen(false)
                }
            } catch (err) {
                setError(`Error: ${err}`)
            } finally {
                setLoading(false)
            }
        },
        [loading, setCurrentStatus, eventId, user.accessLevel, currentStatus],
    )

    return (
        <div>
            {error && <div className="warning-red ta-r">Error updating status</div>}
            <form
                ref={menuRef}
                className={`events-insights-page__status_menu ${hideMobile ? 'hide-mobile' : ''}`}
                style={
                    hasRestrictedAccessFor(user.accessLevel, 'actions', 'canChangeEventStatus')
                        ? { opacity: '0.5' }
                        : {}
                }
            >
                <div
                    onClick={handleMenuOpen}
                    title={
                        hasRestrictedAccessFor(user.accessLevel, 'actions', 'canChangeEventStatus')
                            ? 'You do not have rights to perform this action. Ask an Admin'
                            : ''
                    }
                    value={currentStatus}
                    className={`events-insights-page__status_menu_container ${menuOpen ? 'open' : ''}`}
                >
                    <span className={`events-insights-page__status_menu_selected_value ${menuOpen ? 'open ' : ''}`}>
                        {t(`Dashboard.eventStatusSelect.${currentStatus.toLowerCase()}`)}{' '}
                    </span>

                    <div className="ml-auto d-flex align-items-center gap-1">
                        {currentStatus !== 'DEFAULT' && (
                            <span
                                className="events-insights-page__status_menu_clear"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    handleChange('DEFAULT', e)
                                }}
                            >
                                <RemoveIcon width={14} height={14} />
                            </span>
                        )
                        }

                        {hasRestrictedAccessFor(user.accessLevel, 'actions', 'canChangeEventStatus') ? null : (
                            <div
                                style={{
                                    transitionDuration: '0.2s',
                                    transform: menuOpen ? 'rotate(180deg)' : 'rotate(0)',
                                }}
                                className={`events-insights-page__status_menu_arrow ${menuOpen ? 'open' : ''}`}
                            />)
                        }
                    </div>
                </div>
                <StatusDropdown
                    loading={loading}
                    menuOpen={menuOpen}
                    handleChange={handleChange}
                    currentStatus={currentStatus}
                />
            </form>
        </div>
    )
}

export default StatusSelectMenu
